import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Input,
  Link,
  ListItem,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { FiExternalLink } from 'react-icons/fi'
import useFetch, { CachePolicies } from 'use-http'
import type { ExternalStorageSideMenuRef } from '.'
import { decodeHash, encodeHash, PermissionsEnum } from '../../../utils'
import { pushInBrowserHistory } from '../../../utils/mediaLibrary'
import { originFormInitialValues, SourcesSchema } from '../../../utils/source'
import useToast from '../../hooks/useToast'
import type {
  AddOriginResponse,
  DeleteOriginErrorResponse,
  EndpointFormProps,
  SourcesResponse,
  UrlEndpointsResponse
} from '../../interfaces'
import { MainContentWrapper } from '../../layouts/Base/MainContent'
import { UserContext } from '../../providers'
import { UpgradePrompt } from '../Common'
import FormDirtyPrompt from '../FormDirtyPrompt'
import PermissionGuard from '../PermissionGuard'
import ExternalStorageHeader from './ExternalStorageHeader'
import { InfoPopover } from './InfoPopover'
import { OriginAddedModal } from './OriginAddedModal'
import { OriginDeleteModal } from './OriginDeleteModal'
import OriginFormCheckBox from './OriginFormCheckbox'
import { OriginFormInput } from './OriginFormInput'
import { OriginTypeSelectField } from './OriginTypeSelect'
import UploadGoogleKeyFile from './UploadGoogleKeyFile'
import { sourceFormFields, sourceTypeEnum } from './utils'
import AllowedOriginsDisplay from './AllowedOriginsDisplay'

interface Props {
  sideMenuRef?: React.RefObject<ExternalStorageSideMenuRef>
  urlEndpoints?: UrlEndpointsResponse[]
  refreshUrlEndpoints?(): void
  refreshSources?(): void
}

const ExternalStorageForm = ({
  sideMenuRef,
  urlEndpoints,
  refreshUrlEndpoints,
  refreshSources,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  let { sourceId } = useParams<{ sourceId: string }>()
  sourceId = decodeHash(sourceId) as string

  const {
    get: getSources,
    loading: loadingSources,
    request: sourcesRequest,
    response: sourcesResponse,
  } = useFetch<SourcesResponse>(
    `/api/clients/${user?.clientNumber ?? ''}/sources/${encodeHash(sourceId)}`,
    { cachePolicy: CachePolicies.NO_CACHE },
  )

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const colSpan = useBreakpointValue({ base: 2, xl: 1 })
  const routerHistory = useHistory()
  const routerLocation = useLocation<{
    newEndpoint?: boolean
    urlEndpointId?: string
    formValues?: EndpointFormProps
  } | null>()

  const [initialState, setInitialState] = useState<SourcesResponse | null>(null)

  const formRef = useRef<FormikProps<SourcesResponse>>(null)

  const urlEndpoint = useMemo<UrlEndpointsResponse | undefined>(() => {
    if (urlEndpoints && routerLocation.state?.urlEndpointId) {
      const localUrlEndpoint = urlEndpoints.filter(
        endpoint => endpoint.id === routerLocation.state?.urlEndpointId,
      )[0]

      return localUrlEndpoint
    }
  }, [routerLocation, urlEndpoints])

  const {
    isOpen: isOpenOriginAdded,
    onOpen: onOpenOriginAdded,
    onClose: onCloseOriginAdded,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteOrigin,
    onOpen: onOpenDeleteOrigin,
    onClose: onCloseDeleteOrigin,
  } = useDisclosure()

  const [formError, setFormError] = useState<string>('')
  const [canAddMoreOrigins, setCanAddMoreOrigins] = useState<boolean>(true)

  const getSource = async (): Promise<void> => {
    if (!sourceId) {
      if (routerLocation.state) {
        setInitialState(originFormInitialValues)
      }
      return
    }
    if (sourceId === 'new') {
      setInitialState(originFormInitialValues)
    } else if (user?.clientNumber) {
      const resp = await getSources()

      if (sourcesRequest.error) {
        if (sourcesResponse.status === 404) {
          routerHistory.push('/external-storage')
        } else {
          toast({
            description: t(
              'error_in_getting_origin',
              'An unknown error occured in getting origin details',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        }
      } else {
        setFormError('')
        if (
          resp.typeId === Number(sourceTypeEnum.WEB_FOLDER) &&
          resp.baseUrl === ''
        ) {
          resp.typeId = sourceTypeEnum.WEB_PROXY
        }

        if (
          resp.typeId === Number(sourceTypeEnum.AMAZON_S3) &&
          resp.endpoint === null
        ) {
          delete resp.endpoint
        }

        if (resp.typeId === Number(sourceTypeEnum.AMAZON_S3) && resp.endpoint) {
          resp.typeId = sourceTypeEnum.S3_COMPATIBLE_STORAGE
        }

        const { typeId, ...formFields } = resp

        if (
          typeId === sourceTypeEnum.S3_COMPATIBLE_STORAGE
          && typeof formFields.s3ForcePathStyle === "undefined"
        ) {
          formFields.s3ForcePathStyle = false
        }

        if (
          [
            Number(sourceTypeEnum.AMAZON_S3),
            Number(sourceTypeEnum.S3_COMPATIBLE_STORAGE),
            Number(sourceTypeEnum.CLOUDINARY_BACKUP),
          ].includes(Number(typeId)) &&
          !formFields.s3Prefix
        ) {
          formFields.s3Prefix = '/'
        }

        if (
          Number(sourceTypeEnum.GOOGLE_CLOUD_STORAGE) === Number(typeId) &&
          !formFields.googlePrefix
        ) {
          formFields.googlePrefix = '/'
        }

        if (
          Number(sourceTypeEnum.AZURE) === Number(typeId) &&
          !formFields.azurePrefix
        ) {
          formFields.azurePrefix = '/'
        }

        setInitialState({
          ...originFormInitialValues,
          ...formFields,
          typeId: typeId.toString(),
        })
      }
    }
  }

  useEffect(() => {
    void getSource()
  }, [sourceId, user])

  useEffect(() => {
    const updateHandler = () => {
      const { sourceLength } = window.clientDataStore ?? {};
      if (!user || !sourceLength) return;
      if (user?.pricingPlan.limits.fixed.origins === -1) return;
      if (sourceLength + 1 > user?.pricingPlan.limits.fixed.origins) {
        setCanAddMoreOrigins(false)
      }
    };

    // Run the logic once when the component mounts
    updateHandler();

    window.addEventListener("sourceLengthUpdated", updateHandler);

    return () => {
      window.removeEventListener("sourceLengthUpdated", updateHandler); // Cleanup
    };
  }, []);

  const addNewOrigin = async ({
    payload,
  }: {
    payload: SourcesResponse
  }): Promise<void> => {
    if (!user?.clientNumber) {
      setFormError('')
      onOpenOriginAdded()
      return
    }

    const resp = (await request.post(
      `/api/clients/${user.clientNumber}/sources`,
      payload,
    )) as AddOriginResponse

    if (request.error && resp.reason) {
      setFormError(resp.reason)
      return
    }

    if (request.error) {
      setFormError(
        t(
          'error_in_adding_origin',
          'An unknown error occured in adding new origin',
        ),
      )
      return
    }

    window.trackEvent({ label: 'AddedSource' })

    //
    // When a source is added for a particular existing endpoint.
    //
    if (urlEndpoint && refreshSources) {
      await refreshSources()

      let i = 1
      let key = '1sourcePreference'
      while (key in urlEndpoint && urlEndpoint[key] !== '-1') {
        ++i
        key = `${i}sourcePreference`
      }
      const endpointUpdatePayload = {
        ...urlEndpoint,
        [key]: resp.createdSource.id,
      }

      await request.put(
        `api/clients/${user.clientNumber}/url-patterns/${urlEndpoint.id}`,
        endpointUpdatePayload,
      )

      if (request.error) {
        toast({
          description: t(
            'error_in_adding_origin_with_endpoint',
            'An unknown error occured in attaching the new origin to your endpoint.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        routerHistory.push(
          `/url-endpoints/${routerLocation.state?.urlEndpointId}`,
        )
      } else if (refreshUrlEndpoints) {
        await refreshUrlEndpoints()
        routerHistory.push(
          `/url-endpoints/${routerLocation.state?.urlEndpointId}`,
        )
      }
      return
    }

    // When a source is added while creating a new endpoint.
    // We do not have a urlEndpointId in this case.
    //
    if (routerLocation.state?.newEndpoint && refreshSources) {
      await refreshSources()
      pushInBrowserHistory({
        routerHistory,
        routerLocation,
        path: '/url-endpoints/new',
        state: {
          ...routerLocation.state,
          formValues: {
            ...routerLocation.state.formValues,
            preferenceList:
              routerLocation.state.formValues?.preferenceList.concat([
                resp.createdSource.id,
              ]),
          },
        },
      })
    }

    //
    // When a source is simply added without involvement of an endpoint.
    //
    onOpenOriginAdded()
    routerHistory.push(`/external-storage/${resp.createdSource.id}`)
    await sideMenuRef?.current?.getSources()
  }

  const updateOrigin = async ({
    payload,
    values
  }: {
    payload: SourcesResponse
    values: SourcesResponse
  }): Promise<void> => {
    if (user?.clientNumber) {
      await request.put(
        `/api/clients/${user.clientNumber}/sources/${encodeHash(sourceId)}`,
        payload,
      )

      if (request.error) {
        setFormError(
          t(
            'unknown_error_in_updating_origin',
            'An unknown error occured in updating your origin',
          ),
        )
      } else {
        setInitialState({
          ...initialState,
          ...values
        })
        setFormError('')
        toast({
          description: t(
            'origin_updated_successfully',
            'Your origin has been updated successfully!',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        window.trackEvent({ label: 'AddedSource' })
        await sideMenuRef?.current?.getSources()
      }
    }
  }

  const deleteOrigin = async (): Promise<void> => {
    if (user?.clientNumber) {
      const resp = (await request.del(
        `/api/clients/${user.clientNumber}/sources/${encodeHash(sourceId)}`,
      )) as null | DeleteOriginErrorResponse
      if (request.error) {
        if (resp?.reason) {
          if (
            resp.reason ===
            'ALREADY_PRESENT_IN_ENDPOINT: Cannot delete origin, already present in endpoint: default pattern'
          ) {
            setFormError(
              'Cannot delete origin, already present in endpoint: default pattern',
            )
          } else {
            setFormError(resp.reason)
          }
        } else {
          setFormError(
            t(
              'unknown_error_in_deleting_origin',
              'An unknown error occured in deleting your origin',
            ),
          )
        }
      } else {
        setFormError('')
        toast({
          description: t(
            'origin_deleted_successfully',
            'Your origin has been deleted successfully',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })

        await sideMenuRef?.current?.getSources()
        routerHistory.push('/external-storage')
      }
    }
  }

  return (
    <PermissionGuard
      permission={PermissionsEnum.SELF_INTEGRATION_MANAGE}
    >
      {
        (hasPermission: boolean) => (
          <React.Fragment>
            {!loadingSources && initialState !== null ? (
              <Formik
                enableReinitialize
                validationSchema={SourcesSchema}
                initialValues={initialState}
                onSubmit={async values => {
                  const payload: any = {}

                  const formFields = [
                    ...sourceFormFields.GENERIC,
                    ...sourceFormFields[values.typeId],
                  ]
                  formFields.forEach(field => {
                    const { fieldName } = field
                    payload[fieldName] = values[fieldName]
                  })

                  if (values.includeCanonicalHeader) {
                    payload.includeCanonicalHeader = true
                    payload.baseUrlForCanonicalHeader =
                      values.baseUrlForCanonicalHeader
                  } else {
                    payload.includeCanonicalHeader = false
                  }

                  if (values.typeId === sourceTypeEnum.WEB_FOLDER) {
                    payload.forwardHostHeaderToOrigin =
                      values.forwardHostHeaderToOrigin
                  }

                  if (values.typeId === sourceTypeEnum.WEB_PROXY) {
                    payload.typeId = sourceTypeEnum.WEB_FOLDER
                    payload.baseUrl = ''
                    payload.forwardHostHeaderToOrigin =
                      values.forwardHostHeaderToOrigin
                  } else if (values.typeId === sourceTypeEnum.S3_COMPATIBLE_STORAGE) {
                    payload.typeId = sourceTypeEnum.AMAZON_S3
                    payload.s3ForcePathStyle = values.s3ForcePathStyle ?? false
                  } else {
                    payload.typeId = values.typeId
                  }

                  if (sourceId && sourceId !== 'new') {
                    payload.id = sourceId
                    await updateOrigin({ payload, values })
                  } else {
                    await addNewOrigin({ payload })
                  }
                }}
                innerRef={formRef}
              >
                {({ dirty, isSubmitting, resetForm, values }) => (
                  <React.Fragment>
                    <FormDirtyPrompt isDeleting={isOpenDeleteOrigin} />
                    <Form>
                      <ExternalStorageHeader
                        {...{
                          sourceId,
                          loadingSources,
                          values,
                          dirty,
                          isSubmitting,
                          onOpenDeleteOrigin,
                          setFormError,
                          resetForm,
                        }}
                      />
                      <MainContentWrapper>
                        <Stack direction={['column']} spacing="2.5em">

                          {!canAddMoreOrigins &&
                            <UpgradePrompt message={`On your current plan, you can only have up to ${user?.pricingPlan?.limits?.fixed?.origins} external origin(s). Upgrade your account to add more origins.`} />
                          }

                          {sourceId &&
                            sourceId !== 'new' &&
                            values.associatedPatterns &&
                            values.associatedPatterns.length > 0 && (
                              <Alert status="warning">
                                <AlertIcon />
                                <Stack direction={['column']} spacing="0.75em">
                                  <Box>
                                    {t(
                                      'any_change_in_origin_affects_following_urls',
                                      'Any change in this origin will affect the following URL-endpoint(s):',
                                    )}
                                  </Box>
                                  <Box>
                                    <UnorderedList paddingLeft={1}>
                                      {values.associatedPatterns.map((pattern) => (
                                        <ListItem key={pattern.id}>
                                          <Tooltip label={pattern.name} placement="top" hasArrow>
                                            <Link
                                              href={`/dashboard/url-endpoints/${encodeHash(pattern.id)}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              display="inline-flex"
                                              alignItems="center"
                                              gap="0.25em"
                                              maxWidth="400px"
                                              _hover={{ textDecoration: 'underline' }}
                                            >
                                              <Text
                                                as="strong"
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                whiteSpace="nowrap"
                                              >
                                                {pattern.name}
                                              </Text>
                                              <FiExternalLink size="0.875em" style={{ flexShrink: 0 }} />
                                            </Link>
                                          </Tooltip>
                                          {pattern.domains && pattern.domains.length > 0 && (
                                            <Text as="span" ml="0.5em" fontSize="sm" color="gray.600">
                                              ({pattern.domains.map((domain, index) => (
                                                <React.Fragment key={index}>
                                                  <Text as="span" userSelect="all" cursor="text">
                                                    {domain}
                                                  </Text>
                                                  {index < pattern.domains.length - 1 && ', '}
                                                </React.Fragment>
                                              ))})
                                            </Text>
                                          )}
                                        </ListItem>
                                      ))}
                                    </UnorderedList>
                                  </Box>
                                </Stack>
                              </Alert>
                            )}
                          {sourceId &&
                            sourceId !== 'new' &&
                            (!values.associatedPatterns || values.associatedPatterns.length === 0) && (
                              <Alert status="info">
                                <AlertIcon />
                                <Box>
                                  {t(
                                    'origin_currently_not_used_in_any_url_endpoint',
                                    'This origin is currently not being used in any URL-endpoint.',
                                  )}
                                </Box>
                              </Alert>
                            )}
                          {formError ? (
                            <Alert status="error" mt="1em">
                              <AlertIcon />
                              <AlertDescription>{formError}</AlertDescription>
                            </Alert>
                          ) : null}
                          
                          {sourceId &&
                            sourceId !== 'new' &&
                            values.typeId === sourceTypeEnum.WEB_PROXY &&
                            values.allowedOrigins &&
                            values.allowedOrigins.length > 0 && (
                              <AllowedOriginsDisplay allowedOrigins={values.allowedOrigins} />
                            )}
                          
                          {sourceFormFields.GENERIC.map(field => (
                            <OriginFormInput
                              key={field.fieldName}
                              name={field.fieldName}
                              label={field.label}
                              placeholder={field.placeholder}
                              description={field.description}
                              required={field.required}
                              isDisabled={!hasPermission}
                            />
                          ))}
                          <OriginTypeSelectField isDisabled={!hasPermission} />
                          <SimpleGrid columns={2} columnGap={40} spacingY="36px">
                            {sourceFormFields[values.typeId].map(field => (
                              <GridItem key={field.fieldName} colSpan={colSpan}>
                                <OriginFormInput
                                  name={field.fieldName}
                                  label={field.label}
                                  placeholder={field.placeholder}
                                  info={field.info}
                                  required={field.required}
                                  isDisabled={!hasPermission}
                                />
                              </GridItem>
                            ))}
                          </SimpleGrid>
                          {values.typeId === '5' && <UploadGoogleKeyFile isDisabled={!hasPermission} />}
                          {values.typeId === sourceTypeEnum.CLOUDINARY_BACKUP &&
                            values.cloudinaryListLimit !== undefined &&
                            values.cloudinaryListLimit > 1 && (
                              <FormControl>
                                <Flex alignItems="baseline">
                                  <FormLabel>
                                    {t('cloudinary_list_limit', 'Cloudinary List Limit')}
                                  </FormLabel>
                                  <InfoPopover info="cloudinary_list_limit_info" />
                                </Flex>
                                <Input
                                  value={values.cloudinaryListLimit}
                                  isReadOnly
                                  isDisabled
                                  bg="gray.50"
                                />
                              </FormControl>
                            )}
                          <Stack direction={['column']} spacing="1.5em">
                            <Text fontSize="1em" fontWeight="500">
                              {t('advanced', 'Advanced')}
                            </Text>
                            <OriginFormCheckBox
                              name="includeCanonicalHeader"
                              textFieldName="baseUrlForCanonicalHeader"
                              label={t(
                                'include_canonical_response_header',
                                'Include Canonical Response Header',
                              )}
                              info={'include_canonical_response_header_info'}
                              values={values}
                              isDisabled={!hasPermission}
                            />
                            {values.typeId === sourceTypeEnum.WEB_FOLDER && (
                              <OriginFormCheckBox
                                name="forwardHostHeaderToOrigin"
                                label={t(
                                  'forward_host_header',
                                  'Forward Host Header To This Origin',
                                )}
                                info={'forward_host_header_info'}
                                values={values}
                                isDisabled={!hasPermission}
                              />
                            )}
                            {values.typeId === sourceTypeEnum.S3_COMPATIBLE_STORAGE && (
                              <OriginFormCheckBox
                                name="s3ForcePathStyle"
                                label={t(
                                  'force_path_style_urls',
                                  'Force path-style URL generation',
                                )}
                                info={'force_path_style_urls_info'}
                                values={values}
                                isDisabled={!hasPermission}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </MainContentWrapper>
                    </Form>
                    <OriginDeleteModal
                      isOpenDeleteOrigin={isOpenDeleteOrigin}
                      onCloseDeleteOrigin={onCloseDeleteOrigin}
                      onSubmit={deleteOrigin}
                      originName={values.name}
                    />
                  </React.Fragment>
                )}
              </Formik>
            ) : (
              <Center width="full" height="100%">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Center>
            )}

            <OriginAddedModal
              isOpenOriginAdded={isOpenOriginAdded}
              onCloseOriginAdded={onCloseOriginAdded}
            />
          </React.Fragment>
        )
      }
    </PermissionGuard>
  )
}

export default ExternalStorageForm
